import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { apiGet, apiFetch, apiPost, saveTokens, clearTokens, getStoredToken, getTenantBranding } from "../api";
import { AuthContext, User } from "./useAuth";
import { getGeoFromTimezone } from "../utils/geoInfo";
import { setDisplayPreferences, setTenantTimezone, formatWithTimezone } from "../utils/dateTime";
import { isTenantScoped } from "../utils/permissions";
import BRAND from "../config/branding";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [passwordPolicy, setPasswordPolicy] = useState<any | null>(null);
  const [branding, setBranding] = useState<any | null>(() => {
    try {
      const cached = sessionStorage.getItem("tenant_branding");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [sid, setSid] = useState<string | null>(sessionStorage.getItem("auth_sid"));
  const [pwdExp, setPwdExp] = useState<number | null>(() => {
    const t = getStoredToken();
    if (t) {
      try { return JSON.parse(atob(t.split('.')[1])).pwd_exp || null; } catch { return null; }
    }
    return null;
  });
  const [loading, setLoading] = useState<boolean>(!!token);
  const [lastActivity, setLastActivity] = useState<number>(dayjs().valueOf());
  const silentRefreshTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Geo state: initialized synchronously from IANA timezone (offline, instant)
  const _initGeo = getGeoFromTimezone();
  const [timezone, setTimezone] = useState<string>(_initGeo.timezone);
  const [tenantTimezone, setTenantTimezoneState] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string>(_initGeo.countryCode);
  const [countryName, setCountryName] = useState<string>(_initGeo.countryName);

  // Auto-detect timezone + country on mount
  useEffect(() => {
    const geo = getGeoFromTimezone();
    setTimezone(geo.timezone);
    setCountryCode(geo.countryCode);
    setCountryName(geo.countryName);
  }, []);

  // Sync token state with API layer (handles silent refresh updates)
  useEffect(() => {
    const handleSync = (e: any) => {
      const newToken = e.detail.accessToken;
      if (newToken !== token) {
        setToken(newToken);
      }
    };

    window.addEventListener('auth-token-synced', handleSync);
    return () => window.removeEventListener('auth-token-synced', handleSync);
  }, [token]);

  // Sync PwdExp automatically whenever token changes natively
  useEffect(() => {
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setPwdExp(decoded.pwd_exp || null);
      } catch (e) {
        setPwdExp(null);
      }
    } else {
      setPwdExp(null);
    }
  }, [token]);

  // Proactive silent refresh: schedule at 80% of token lifetime.
  // This runs whenever a new token arrives (login or refresh response).
  // It reads `auth_expires_in` (seconds) stored at login time.
  const scheduleProactiveRefresh = useCallback(() => {
    if (silentRefreshTimerRef.current) clearTimeout(silentRefreshTimerRef.current);
    const expiresIn = parseInt(sessionStorage.getItem("auth_expires_in") || "900", 10); // default 15min
    const refreshIn = Math.max(expiresIn * 0.8 * 1000, 30_000); // at least 30s, 80% of lifetime
    console.debug(`[AuthContext] Proactive refresh scheduled in ${Math.round(refreshIn / 1000)}s`);
    silentRefreshTimerRef.current = setTimeout(async () => {
      const { refreshAccessToken, saveTokens } = await import('../api');
      const newToken = await refreshAccessToken();
      if (newToken) {
        saveTokens(newToken, null);
        scheduleProactiveRefresh(); // re-schedule for next cycle
      }
    }, refreshIn);
  }, []);

  const resolvePostLoginTarget = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const redirect = searchParams.get('redirectUrl');
    if (redirect) return redirect;
    if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
      return window.location.pathname + window.location.search;
    }
    return "/";
  }, []);

  // Cleanup refresh timer on unmount
  useEffect(() => {
    return () => {
      if (silentRefreshTimerRef.current) clearTimeout(silentRefreshTimerRef.current);
    };
  }, []);

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? 
      `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}` : 
      null;
  };

  const applyTenantBranding = useCallback(async (tenantId: string, authToken: string) => {
    try {
      const brandingData = await getTenantBranding(authToken);

      // Custom CSS injection
      if (brandingData.custom_css) {
        let styleTag = document.getElementById('tenant-custom-css');
        if (!styleTag) {
          styleTag = document.createElement('style');
          styleTag.id = 'tenant-custom-css';
          document.head.appendChild(styleTag);
        }
        styleTag.innerHTML = brandingData.custom_css;
      }

      setBranding(brandingData);
      sessionStorage.setItem("tenant_branding", JSON.stringify(brandingData));
      
      // Scoping: Ensure the UI identifies as a tenant-branded session
      document.documentElement.classList.add('tenant-portal');

      // Standardize: Branding timezone takes priority for the UI
      if (brandingData.timezone || brandingData.date_format || brandingData.time_format) {
        setDisplayPreferences({
          timezone: brandingData.timezone,
          date_format: brandingData.date_format,
          time_format: brandingData.time_format
        });
        setTenantTimezoneState(brandingData.timezone || null);
      }
    } catch (brandingErr) {
      // Failed to apply branding
    }
  }, []);

  // Fetch user when token changes
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        if (user) setUser(null);
        setLoading(false);
        return;
      }

      // Skip network fetch if user is already populated from login() or loginWithToken().
      // This avoids the double /users/me round-trip on every login.
      if (user) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiGet("/users/me", token);
        const data = response.user;
        const policy = response.password_policy;
        const display_prefs = response.display_preferences;
        
        if (display_prefs) {
          // Sync with utility (AuthContext is the orchestrator)
          setDisplayPreferences(display_prefs, data);
          setTenantTimezoneState(display_prefs.timezone || null);
        }
        
        setUser(prev => {
          if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
          return data;
        });

        setPasswordPolicy(prev => {
          if (JSON.stringify(prev) === JSON.stringify(policy)) return prev;
          return policy;
        });

        // ── BRANDING FETCH (NEW) ──────────────────────────────────────
        if (data?.tenantId && isTenantScoped(data)) {
          await applyTenantBranding(data.tenantId, token);
        } else {
          setBranding(null);
          sessionStorage.removeItem("tenant_branding");
          document.documentElement.classList.remove('tenant-portal');
          const styleTag = document.getElementById('tenant-custom-css');
          if (styleTag) styleTag.remove();

          // Reset document title and favicon to platform defaults
          // This prevents tenant assets from leaking into the global portal
          document.title = BRAND.name;
          document.querySelectorAll("link[rel~='icon']").forEach(e => e.remove());
          const defaultFavicon = document.createElement('link');
          defaultFavicon.rel = 'icon';
          defaultFavicon.id = 'platform-favicon';
          defaultFavicon.href = BRAND.faviconSrc || '/favicon-32x32.png';
          document.head.appendChild(defaultFavicon);
        }
      } catch (err) {
        console.warn("[AuthContext] Invalid or expired token:", err);
        clearTokens();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [token]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiGet("/users/me", token);
      const data = response.user;
      const policy = response.password_policy;
      const display_prefs = response.display_preferences;

      if (display_prefs) {
        setDisplayPreferences(display_prefs, data);
        setTenantTimezoneState(display_prefs.timezone || null);
      }

      setUser(prev => {
        if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
        return data;
      });

      setPasswordPolicy(prev => {
        if (JSON.stringify(prev) === JSON.stringify(policy)) return prev;
        return policy;
      });

      if (data?.tenantId && isTenantScoped(data)) {
        await applyTenantBranding(data.tenantId, token);
      } else {
        setBranding(null);
        sessionStorage.removeItem("tenant_branding");
        document.documentElement.classList.remove('tenant-portal');
        const styleTag = document.getElementById('tenant-custom-css');
        if (styleTag) styleTag.remove();

        // Reset document title and favicon to platform defaults
        document.title = BRAND.name;
        document.querySelectorAll("link[rel~='icon']").forEach(e => e.remove());
        const defaultFavicon = document.createElement('link');
        defaultFavicon.rel = 'icon';
        defaultFavicon.id = 'platform-favicon';
        defaultFavicon.href = BRAND.faviconSrc || '/favicon-32x32.png';
        document.head.appendChild(defaultFavicon);
      }
    } catch (err) {
      console.warn("[AuthContext] Failed to refresh user:", err);
    }
  }, [token]);

  // ---  FIXED LOGIN FUNCTION ---
  const login = useCallback(async (username: string, password: string) => {
    try {
      const formBody = new URLSearchParams();
      formBody.append("username", username);
      formBody.append("password", password);

      const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
      const cleanBase = API_BASE.trim().replace(/\/+$/, '');
      const fetchUrl = `${cleanBase.replace(/\/api\/v1\/?$/, '')}/api/v1/auth/token`;

      const rawRes = await fetch(fetchUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody.toString(),
        credentials: "include",
      });

      let responseData: any;
      try { responseData = await rawRes.json(); } catch { responseData = {}; }

      if (!rawRes.ok) {
        if (!(rawRes.status === 403 && (responseData?.mfa_required || responseData?.password_expired))) {
          const msg = responseData.detail || "Login failed";
          throw new Error(typeof msg === 'object' ? JSON.stringify(msg) : String(msg));
        }
      }

      const response = responseData;
      
      if (response?.password_expired) {
        throw {
          isPasswordExpired: true,
          message: response.detail
        };
      }
      
      if (response?.mfa_required) {
        throw {
          isMfaRequired: true,
          mfaSessionToken: response.mfa_session_token,
          mfaMethod: response.mfa_method
        };
      }

      if (response?.ssa_pending) {
        throw {
          isSsaPending: true,
          requestId: response.request_id,
          timeout: response.timeout
        };
      }

      const accessToken = response?.access_token;
      const refreshToken = response?.refresh_token;
      const sessionSid = response?.sid;
      const expiresIn = response?.expires_in; // seconds, from server (tenant-configured)

      if (!accessToken) throw new Error("Login failed: No access token returned");

      saveTokens(accessToken, refreshToken);
      if (expiresIn) sessionStorage.setItem("auth_expires_in", String(expiresIn));
      if (sessionSid) {
        sessionStorage.setItem("auth_sid", sessionSid);
        setSid(sessionSid);
      }
      resetInactivity();
      scheduleProactiveRefresh();
      setToken(accessToken);

      const response_me = await apiGet("/users/me", accessToken);
      const data = response_me.user;
      setUser(data);
      setPasswordPolicy(response_me.password_policy);

      if (data?.tenantId && isTenantScoped(data)) {
        await applyTenantBranding(data.tenantId, accessToken);
      } else {
        setBranding(null);
        sessionStorage.removeItem("tenant_branding");
        document.documentElement.classList.remove('tenant-portal');
        const styleTag = document.getElementById('tenant-custom-css');
        if (styleTag) styleTag.remove();
      }

      if (response_me.display_preferences) {
        setDisplayPreferences(response_me.display_preferences, data);
        setTenantTimezoneState(response_me.display_preferences.timezone || null);
      }

      // Navigate to intended page or root
      navigate(resolvePostLoginTarget());
    } catch (err: any) {
      if (err.isMfaRequired) throw err;
      console.error("[AuthContext] Login failed:", err);
      throw err;
    }
  }, [navigate, resolvePostLoginTarget]);

  // --- MFA VERIFICATION ---
  const verifyMfa = useCallback(async (mfaSessionToken: string, otpCode: string) => {
    try {
      const response = await apiFetch("/auth/verify-mfa", null, {
        method: "POST",
        body: JSON.stringify({
          mfa_session_token: mfaSessionToken,
          otp_code: otpCode
        }),
      });

      const accessToken = response?.access_token;
      const refreshToken = response?.refresh_token;
      const sessionSid = response?.sid;
      const expiresIn = response?.expires_in;

      if (!accessToken) throw new Error("Verification failed: No token returned");

      saveTokens(accessToken, refreshToken);
      if (expiresIn) sessionStorage.setItem("auth_expires_in", String(expiresIn));
      if (sessionSid) {
        sessionStorage.setItem("auth_sid", sessionSid);
        setSid(sessionSid);
      }
      resetInactivity();
      scheduleProactiveRefresh();
      setToken(accessToken);

      const response_me = await apiGet("/users/me", accessToken);
      setUser(response_me.user);
      setPasswordPolicy(response_me.password_policy);
      if (response_me.display_preferences) {
        setDisplayPreferences(response_me.display_preferences, response_me.user);
        setTenantTimezoneState(response_me.display_preferences.timezone || null);
      }
      const searchParams = new URLSearchParams(window.location.search);
      const redirect = searchParams.get('redirectUrl');
      if (redirect) {
        navigate(redirect);
      } else if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
        navigate(window.location.pathname + window.location.search);
      } else {
        navigate("/");
      }
    } catch (err) {
      console.error("[AuthContext] MFA Verification Failed:", err);
      throw err;
    }
  }, [navigate, resolvePostLoginTarget]);

  // --- MFA RESEND ---
  const resendMfa = useCallback(async (mfaSessionToken: string) => {
    try {
      await apiFetch("/auth/resend-mfa-code", null, {
        method: "POST",
        body: JSON.stringify({ mfa_session_token: mfaSessionToken }),
      });
    } catch (err) {
      console.error("[AuthContext] MFA Resend Failed:", err);
      throw err;
    }
  }, []);

  const requestMagicLink = useCallback(async (email: string) => {
    try {
      await apiFetch("/auth/login", null, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      console.error("[AuthContext] Magic link request failed:", err);
      throw err;
    }
  }, []);

  const loginWithMagicToken = useCallback(async (magicToken: string) => {
    try {
      const response = await apiFetch("/auth/magic-token", null, {
        method: "POST",
        body: JSON.stringify({ magic_token: magicToken }),
      });

      // SSA (Single Session Approval) — same as password login flow
      if (response?.ssa_pending) {
        throw {
          isSsaPending: true,
          requestId: response.request_id,
          timeout: response.timeout
        };
      }

      const accessToken = response?.access_token;
      const refreshToken = response?.refresh_token;
      const sessionSid = response?.sid;
      const expiresIn = response?.expires_in;

      if (!accessToken) throw new Error("Login failed: No access token returned");

      saveTokens(accessToken, refreshToken);
      if (expiresIn) sessionStorage.setItem("auth_expires_in", String(expiresIn));
      if (sessionSid) {
        sessionStorage.setItem("auth_sid", sessionSid);
        setSid(sessionSid);
      }
      resetInactivity();
      scheduleProactiveRefresh();
      setToken(accessToken);

      const response_me = await apiGet("/users/me", accessToken);
      setUser(response_me.user);
      setPasswordPolicy(response_me.password_policy);
      if (response_me.display_preferences) {
        setDisplayPreferences(response_me.display_preferences, response_me.user);
        setTenantTimezoneState(response_me.display_preferences.timezone || null);
      }
      navigate("/");
    } catch (err: any) {
      if (err.isSsaPending) throw err;  // Let Auth.jsx handle the SSA flow
      console.error("[AuthContext] Magic token login failed:", err);
      throw err;
    }
  }, [navigate, resolvePostLoginTarget]);


  // ---  FIXED LOGOUT FUNCTION ---
  const logout = useCallback(async () => {
    if (token) {
      try {
        await apiPost("/auth/logout", token, {});
      } catch (err) {
        console.warn("[AuthContext] Backend logout failed:", err);
      }
    }

    clearTokens();
    sessionStorage.removeItem("auth_sid");
    setSid(null);
    setToken(null);
    setUser(null);
    setPasswordPolicy(null);
    setBranding(null);
    sessionStorage.removeItem("tenant_branding");
    document.documentElement.classList.remove('tenant-portal');
    document.documentElement.classList.remove('dark');
    setDisplayPreferences(null, null);
    setTenantTimezoneState(null);
    const styleTag = document.getElementById('tenant-custom-css');
    if (styleTag) styleTag.remove();

    // Reset CSS variables to default platform theme to prevent
    // tenant branding from leaking into the login/forgot-password screens
    const root = document.documentElement;
    root.style.setProperty('--primary-color', '#3b82f6');
    root.style.setProperty('--primary-rgb', '59 130 246');
    root.style.setProperty('--secondary-color', '#64748b');
    root.style.setProperty('--secondary-rgb', '100 116 139');
    root.style.setProperty('--accent-color', '#f59e0b');
    root.style.setProperty('--accent-rgb', '245 158 11');
    root.style.setProperty('--font-family', "'Inter', sans-serif");
    root.style.setProperty('--border-radius', '8px');
    root.style.setProperty('--sidebar-bg', '#ffffff');
    root.style.setProperty('--sidebar-text', '#1e293b');
    root.style.setProperty('--header-bg', '#ffffff');

    // Reset document title and favicon to platform defaults
    document.title = BRAND.name;
    document.querySelectorAll("link[rel~='icon']").forEach(e => e.remove());
    const defaultFavicon = document.createElement('link');
    defaultFavicon.rel = 'icon';
    defaultFavicon.id = 'platform-favicon';
    defaultFavicon.href = BRAND.faviconSrc || '/favicon-32x32.png';
    document.head.appendChild(defaultFavicon);

    const current = window.location.pathname.toLowerCase();
    if (current.startsWith("/tenant")) {
      navigate("/tenant/login");
    } else {
      navigate("/login");
    }
  }, [navigate, token]);

  const loginWithToken = useCallback(async (accessToken: string, sessionSid?: string) => {
    setLoading(true);
    try {
      saveTokens(accessToken, null);
      resetInactivity(); // Reset timer before starting session monitoring
      setToken(accessToken);

      if (sessionSid) {
        sessionStorage.setItem("auth_sid", sessionSid);
        setSid(sessionSid);
      }

      const response_me = await apiGet("/users/me", accessToken);
      setUser(response_me.user);
      setPasswordPolicy(response_me.password_policy);
      if (response_me.display_preferences) {
        setDisplayPreferences(response_me.display_preferences, response_me.user);
        setTenantTimezoneState(response_me.display_preferences.timezone || null);
      }
      navigate("/");
    } catch (err) {
      console.error("[AuthContext] Token login failed:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [navigate, resolvePostLoginTarget]);

  const resetInactivity = useCallback(() => {
    setLastActivity(dayjs().valueOf());
  }, []);

  const value = React.useMemo(() => ({
    user,
    token,
    sid,
    pwdExp,
    loading,
    login,
    verifyMfa,
    resendMfa,
    requestMagicLink,
    loginWithMagicToken,
    loginWithToken,
    logout,
    refreshUser,
    passwordPolicy,
    branding,
    lastActivity,
    resetInactivity,
    timezone,
    tenantTimezone,
    countryCode,
    countryName,
  }), [user, token, sid, pwdExp, loading, login, verifyMfa, resendMfa, requestMagicLink, loginWithMagicToken, loginWithToken, logout, refreshUser, passwordPolicy, branding, lastActivity, resetInactivity, timezone, tenantTimezone, countryCode, countryName]);

  useEffect(() => {
    if (user && user.preferences) {
      setDisplayPreferences(user.preferences, user);
    } else if (user && user.tenantTimezone) {
      setTenantTimezone(user.tenantTimezone);
    } else {
      setDisplayPreferences(null, null);
    }
  }, [user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};